import { Location } from '@angular/common';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { WINDOW } from '@campus/browser';
import {
  DalState,
  DataActions,
  EduContentHighScoreService,
  EduContentQueries,
  LearningAreaActions,
  LearningEventVerbEnum,
  LearningRecordActions,
  LearningRecordInterface,
  LearningRecordService,
  PersonService,
  RegistrationInterface,
  RegistrationService,
  RegistrationTypeEnum,
  SchoolRoleMappingQueries,
  USER_GAME_PROFILE_SERVICE,
  UserGameProfileServiceInterface,
  UserQueries,
} from '@campus/dal';
import { ENVIRONMENT_CROSS_ORIGIN_TOKEN } from '@campus/environment';
import { select, Store } from '@ngrx/store';
import { combineLatest, forkJoin, fromEvent, Observable, of, Subject, Subscription, withLatestFrom } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import {
  GameCompletedMessage,
  GameEarningMessage,
  GameExerciseMessage,
  GameGetKwetonsMessage,
  GameGetProfileDataMessage,
  GameLearningEventsServiceInterface,
  GameLoadedMessage,
  GameMessage,
  GameMessageType,
  GameOpenInstructionsMessage,
  GameProfileDataSaveMessage,
  GamePurchaseMessage,
  GameRestartedMessage,
  GameWindowClosedMessage,
} from './game-learning-events.service.interface';

export interface RegistrationWithHighScoreInterface extends RegistrationInterface {
  highScore: number;
}

@Injectable({
  providedIn: 'root',
})
export class GameLearningEventsService implements GameLearningEventsServiceInterface, OnDestroy {
  private gameMessageTypes: Set<string>;
  private gameMessages$: Observable<GameMessage>;

  private subscriptions = new Subscription();

  private window = inject(WINDOW);
  private registrationService = inject(RegistrationService);
  private store: Store<DalState> = inject(Store);
  private eduContentHighScoreService = inject(EduContentHighScoreService);
  private learningRecordService = inject(LearningRecordService);
  private userGameProfileService: UserGameProfileServiceInterface =
    inject<UserGameProfileServiceInterface>(USER_GAME_PROFILE_SERVICE);
  private router = inject(Router);
  private location = inject(Location);
  private personService = inject(PersonService);
  private crossOrigin = inject(ENVIRONMENT_CROSS_ORIGIN_TOKEN, { optional: true });

  private _destroy$ = new Subject<void>();

  constructor() {
    this.gameMessageTypes = new Set(Object.values(GameMessageType));

    this.setupStreams();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();

    this._destroy$.next();
    this._destroy$.complete();
  }

  registerEventListener() {
    this.subscriptions.add(this.gameMessages$.subscribe((gameMessage) => this.handleGameMessage(gameMessage)));
  }

  private setupStreams() {
    this.gameMessages$ = fromEvent(this.window, 'message').pipe(
      filter((event: MessageEvent) => this.validateGameMessage(event)),
      map((event: MessageEvent) => {
        return {
          ...event.data,
          sourceWindow: event.source,
          sourceOrigin: event.origin,
        };
      })
    );
  }

  private handleGameMessage(message: GameMessage) {
    switch (message.type) {
      case GameMessageType.GAME_LOADED:
        return this.handleGameLoadedMessage(message);
      case GameMessageType.GAME_RESTARTED:
        return this.handleGameRestartedMessage(message);
      case GameMessageType.GAME_COMPLETED:
        return this.handleGameCompletedMessage(message);
      case GameMessageType.GAME_EXERCISE:
        return this.handleGameExerciseMessage(message);
      case GameMessageType.GAME_WINDOW_CLOSED:
        return this.handleGameWindowClosedMessage(message);
      case GameMessageType.GAME_OPEN_INSTRUCTIONS:
        return this.handleGameOpenInstructionsMessage(message);
      case GameMessageType.GAME_GET_PROFILE_DATA:
        return this.handleGetProfileData(message);
      case GameMessageType.GAME_SAVE_PROFILE_DATA:
        return this.handleSaveUserGameProfile(message);
      case GameMessageType.GAME_GET_KWETONS:
        return this.handleKwetonMessage(message);
      case GameMessageType.GAME_PURCHASE:
        return this.handleGamePurchaseMessage(message);
      case GameMessageType.GAME_EARNING:
        return this.handleGameEarningMessage(message);
      default:
        console.warn('[GameLearningEventsService] handleGameMessage: unknown message type', message);
    }
  }

  private handleGameLoadedMessage(message: GameLoadedMessage) {
    this.replyWithRegistration(message)
      .pipe(filter((registration) => !!registration))
      .subscribe((registration) => {
        this.store.dispatch(
          new LearningRecordActions.SaveLearningRecord({
            learningRecord: {
              registrationId: registration.id,
              verb: LearningEventVerbEnum.EXPERIENCED,
              duration: 0,
              eduContentId: registration.contentId,
              completion: false,
            },
          })
        );
      });
  }

  private handleGameRestartedMessage(message: GameRestartedMessage) {
    this.replyWithRegistration(message)
      .pipe(filter((registration) => !!registration))
      .subscribe((registration) => {
        this.store.dispatch(
          new LearningRecordActions.SaveLearningRecord({
            learningRecord: {
              registrationId: registration.id,
              verb: LearningEventVerbEnum.EXPERIENCED,
              duration: 0,
              eduContentId: registration.contentId,
              completion: false,
            },
          })
        );
      });
  }

  private handleGameCompletedMessage(message: GameCompletedMessage) {
    this.store
      .select(UserQueries.getCurrentUser)
      .pipe(
        takeUntil(this._destroy$),
        filter((user) => (user?.roles || []).some((role) => role.name === 'student')),
        switchMap((user) => {
          const highScoreUpdate$ = this.eduContentHighScoreService.updateHighScoreForEduContent(
            message.data.eduContentId,
            message.data.score
          );

          const learningRecordUpdate$ = this.learningRecordService.saveLearningRecord({
            registrationId: message.data.registrationId,
            verb: LearningEventVerbEnum.COMPLETED,
            score: Math.round((message.data.correctExercises / message.data.totalExercises) * 100),
            rawScore: message.data.correctExercises,
            maxScore: message.data.totalExercises,
            duration: message.data.playTime,
            eduContentId: message.data.eduContentId,
            completion: true,
            earnedKwetons: message.data.kwetonCount,
          } as LearningRecordInterface);

          return combineLatest([of(user), highScoreUpdate$, learningRecordUpdate$]);
        }),
        take(1),
        tap(([user]) => {
          this.store.dispatch(new LearningAreaActions.LoadLearningAreas({ force: true }));
          this.store.dispatch(
            new DataActions.LoadData({ force: true, userId: user.id, fields: ['schoolRoleMappings'] })
          );
        })
      )
      .subscribe(() => {
        this.replyToGameMessage(message, {
          type: GameMessageType.GAME_COMPLETED_PROCESSED,
          data: undefined,
        });
      });
  }

  private handleGameExerciseMessage(message: GameExerciseMessage) {
    this.store
      .select(UserQueries.getCurrentUser)
      .pipe(
        takeUntil(this._destroy$),
        filter((user) => (user?.roles || []).some((role) => role.name === 'student')),
        take(1)
      )
      .subscribe(() => {
        this.store.dispatch(
          new LearningRecordActions.SaveLearningRecord({
            learningRecord: {
              registrationId: message.data.registrationId,
              verb: LearningEventVerbEnum.ANSWERED,
              duration: message.data.playTime,
              eduContentId: message.data.eduContentId,
              completion: false,
              success: message.data.correctAnswer,
            },
          })
        );
      });
  }

  private handleGameWindowClosedMessage(message: GameWindowClosedMessage) {
    // nothing for now
  }

  private handleGameOpenInstructionsMessage(message: GameOpenInstructionsMessage) {
    const { eduContentId } = message.data;

    this.store
      .pipe(select(EduContentQueries.getById(eduContentId)))
      .pipe(take(1))
      .subscribe((eduContent) => {
        const { artifactId } = eduContent.publishedEduContentMetadata;

        const path = this.router.createUrlTree(['/content', 'instructions', artifactId]).toString();
        const fullPath = this.location.prepareExternalUrl(path);
        const url = location.origin + fullPath;

        this.replyToGameMessage(message, {
          type: GameMessageType.GAME_OPEN_URL,
          data: {
            url,
          },
        });
      });
  }

  private replyToGameMessage(sourceMessage: GameMessage, message: GameMessage) {
    sourceMessage.sourceWindow.postMessage(message, sourceMessage.sourceOrigin);
  }

  private replyWithRegistration(sourceMessage: GameLoadedMessage | GameRestartedMessage) {
    return this.store.select(UserQueries.getCurrentUser).pipe(
      takeUntil(this._destroy$),
      switchMap((user) => {
        if ((user?.roles || []).some((role) => role.name === 'student')) {
          return this.createNewRegistration(sourceMessage.data.eduContentId);
        }
        return of(null);
      }),
      withLatestFrom(this.store.select(EduContentQueries.getById(sourceMessage.data.eduContentId))),
      tap(([registration, eduContent]) => {
        const content = eduContent.publishedEduContentMetadata.data;

        if (!registration) {
          // Teacher flow - no registration
          this.replyToGameMessage(sourceMessage, {
            type: GameMessageType.GAME_REGISTRATION,
            data: {
              registrationId: -1, //teachers don't have a registration
              eduContentId: eduContent.id,
              currentHighscore: 0,
              ...(content && { content }),
            },
          });
        } else {
          // Student flow - with registration
          this.replyToGameMessage(sourceMessage, {
            type: GameMessageType.GAME_REGISTRATION,
            data: {
              registrationId: registration.id,
              eduContentId: registration.contentId,
              //Add userId if there is content
              // userId: registration.userId,
              // content: see 25368 (json)
              currentHighscore: registration.highScore,
              ...(content && { content }),
            },
          });
        }
      }),
      map(([registration]) => registration)
    );
  }

  private handleGetProfileData(message: GameGetProfileDataMessage) {
    this.userGameProfileService
      .getUserGameProfiles(message.data.keys, message.data.methodCode)
      .pipe(
        take(1),
        map((profileData) => {
          this.replyToGameMessage(message, {
            type: GameMessageType.GAME_PROFILE_DATA,
            data: {
              profileData,
            },
          });
        })
      )
      .subscribe();
  }

  private handleSaveUserGameProfile(message: GameProfileDataSaveMessage) {
    this.userGameProfileService
      .saveUserGameProfile(message.data.profileData)
      .pipe(
        take(1),
        tap((profileData) => {
          this.replyToGameMessage(message, {
            type: GameMessageType.GAME_PROFILE_DATA_SAVED,
            data: {
              profileData,
            },
          });
        })
      )
      .subscribe();
  }

  private handleKwetonMessage(message: GameGetKwetonsMessage) {
    combineLatest([this.store.select(UserQueries.getCurrentUser), this.store.select(SchoolRoleMappingQueries.getAll)])
      .pipe(
        map(([currentUser, schoolRoleMappings]) => {
          if (!currentUser) return;
          const userId = currentUser.id;
          const isTeacher = currentUser.roles.some((role) => role.name === 'teacher');
          if (isTeacher) return { kwetons: 1000 };
          const srmForUser = schoolRoleMappings.find((srm) => srm.personId === userId);

          const gameCoins = (srmForUser && srmForUser.gameCoins) || 0;
          return { kwetons: gameCoins };
        }),
        take(1)
      )
      .subscribe((data) => {
        this.replyToGameMessage(message, {
          type: GameMessageType.GAME_USER_KWETONS,
          data,
        });
      });
  }

  private handleGamePurchaseMessage(message: GamePurchaseMessage) {
    let { price } = message.data;
    price = price > 0 ? -price : price;
    this.personService
      .purchaseWithKwetons(price)
      .pipe(withLatestFrom(this.store.select(UserQueries.getCurrentUser)))
      .pipe(take(1))
      .subscribe({
        next: ([response, user]) => {
          this.store.dispatch(
            new DataActions.LoadData({ userId: user.id, fields: ['schoolRoleMappings'], force: true })
          );
          this.replyToGameMessage(message, {
            type: GameMessageType.GAME_USER_PURCHASED,
            data: {
              kwetons: response.kwetons,
            },
          });
        },
        error: (err: Error) => {
          this.replyToGameMessage(message, {
            type: GameMessageType.GAME_USER_PURCHASE_FAILED,
            data: {
              error: err.message,
            },
          });
        },
      });
  }

  private handleGameEarningMessage(message: GameEarningMessage) {
    const { amount } = message.data;
    this.personService
      .purchaseWithKwetons(amount)
      .pipe(withLatestFrom(this.store.select(UserQueries.getCurrentUser)))
      .pipe(take(1))
      .subscribe({
        next: ([response, user]) => {
          this.store.dispatch(
            new DataActions.LoadData({ userId: user.id, fields: ['schoolRoleMappings'], force: true })
          );
          this.replyToGameMessage(message, {
            type: GameMessageType.GAME_USER_EARNED,
            data: {
              kwetons: response.kwetons,
            },
          });
        },
        error: (err: Error) => {
          this.replyToGameMessage(message, {
            type: GameMessageType.GAME_USER_EARNING_FAILED,
            data: {
              error: err.message,
            },
          });
        },
      });
  }

  private createNewRegistration(contentId: number): Observable<RegistrationWithHighScoreInterface> {
    return forkJoin([
      this.eduContentHighScoreService.getHighScoreForEduContent(contentId),
      this.registrationService.createRegistration({
        contentId,
        type: RegistrationTypeEnum.GAME,
      }),
    ]).pipe(
      takeUntil(this._destroy$),
      map(([highScore, registration]) => {
        return {
          ...registration,
          highScore: highScore?.score || 0,
        };
      })
    );
  }

  private validateGameMessage(event: MessageEvent): boolean {
    const trustedSuffix = this.crossOrigin?.trustedSuffix;
    const originIsCorrect = trustedSuffix
      ? event.origin.endsWith(trustedSuffix)
      : event.origin === this.window.location.origin;
    const isGameMessage = event.data?.type && this.gameMessageTypes.has(event.data.type);

    return originIsCorrect && isGameMessage;
  }
}
